import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import TollIcon from '@mui/icons-material/Toll';
import { alpha, keyframes, lighten, styled, useTheme } from '@mui/material/styles';
import type BalanceStore from '../../../store/balance/balanceStore';
import type PurchaseStore from '../../../store/purchase/purchaseStore';
import { formatCount } from '../../../features/purchase/money';
import currency from '../../../assets/images/currentWallet.png';

/** The unit after a count: `1 credit`, otherwise `credits`. */
const creditsUnit = (credits: number): string => (Math.abs(credits) === 1 ? 'credit' : 'credits');

/** A change in the total, signed: `+100`, or `−3` with the minus sign the Norwegian grouping writes. */
const changeLabel = (delta: number): string => (delta > 0 ? `+${formatCount(delta)}` : formatCount(delta));

/** The renewal is at midnight UTC, so the day is read in UTC; read locally it could show the day before. */
const renewalDateLabel = (nextRenewalUtc: string): string => new Date(nextRenewalUtc).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });

const BREAKDOWN_ID = 'credits-breakdown';

/** What a trial account, or one without a role, is shown where the buy action would be. */
export const PAID_ROLE_REQUIRED_MESSAGE = 'Purchases are available on a paid plan.';

/** How long a change stays under the total, from appearing to gone: long enough to read a number, short enough to be feedback rather than a notice. */
export const CHANGE_VISIBLE_MS = 700;

/** A change in the total, keyed so that a change which replaces one still in view restarts its motion. */
interface ICreditChange {
  key: number;
  delta: number;
}

/**
 * The change in the total each time it changes, for as long as it is shown; null in between. The
 * first total after none — the sign-in, or a change of whose credits these are — is no change. A
 * change that lands while one is still in view is added to it, so what floats under the total is
 * how far it has moved since the author last saw it settled.
 */
const useCreditChange = (totalCredits: number | null): ICreditChange | null => {
  const [change, setChange] = useState<ICreditChange | null>(null);
  const previousTotal = useRef<number | null>(null);
  const nextKey = useRef(0);

  useEffect(() => {
    const before = previousTotal.current;
    previousTotal.current = totalCredits;
    if (before === null || totalCredits === null || totalCredits === before) return;
    const delta = totalCredits - before;
    nextKey.current += 1;
    const key = nextKey.current;
    setChange((inView) => {
      const sum = (inView?.delta ?? 0) + delta;
      return sum === 0 ? null : { key, delta: sum };
    });
  }, [totalCredits]);

  useEffect(() => {
    if (change === null) return;
    const timer = window.setTimeout(() => setChange(null), CHANGE_VISIBLE_MS);
    return () => window.clearTimeout(timer);
  }, [change]);

  return change;
};

/** Credits leaving: the change starts right under the total and sinks away from it. */
const sink = keyframes`
  from { transform: translate(-50%, 0); }
  to { transform: translate(-50%, 10px); }
`;

/** Credits arriving: the change rises from below and settles right under the total. */
const rise = keyframes`
  from { transform: translate(-50%, 10px); }
  to { transform: translate(-50%, 0); }
`;

/** In quickly, held while it is read, out as it reaches the end of its travel. */
const fade = keyframes`
  0% { opacity: 0; }
  15% { opacity: 1; }
  60% { opacity: 1; }
  100% { opacity: 0; }
`;

/**
 * The change floating under the total. It lives inside the button so it is centred under the
 * number itself, not under the icon and the number together; it takes no clicks and is not read
 * out, since the total it sits under has already changed.
 */
const FloatingChange = styled('span', { shouldForwardProp: (prop) => prop !== 'delta' })<{ delta: number }>(({ theme, delta }) => {
  // The theme's error red is too dark on the bar for small text; lifted a quarter of the way to white it reads at 4.9:1. Trimmed, since the theme's value carries trailing spaces.
  const color = delta < 0 ? lighten(theme.palette.error.main.trim(), 0.25) : theme.palette.success.main.trim();
  const travel = delta < 0 ? sink : rise;
  return {
    position: 'absolute',
    left: '50%',
    top: 'calc(100% - 4px)',
    transform: 'translate(-50%, 0)',
    color,
    fontSize: 11,
    lineHeight: '14px',
    fontWeight: 700,
    letterSpacing: '0.02em',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    opacity: 0,
    animation: `${travel} ${CHANGE_VISIBLE_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1), ${fade} ${CHANGE_VISIBLE_MS}ms linear`,
    animationFillMode: 'forwards',
    '@media (prefers-reduced-motion: reduce)': {
      animation: `${fade} ${CHANGE_VISIBLE_MS}ms linear`,
    },
  };
});

/** The breakdown's width: room for `Monthly credits renew 15 September 2027` on one line, and for the illustration to read. */
const BREAKDOWN_WIDTH = 300;

/** The height of the panel the total sits on, over the illustration. */
const PANEL_HEIGHT = 118;

/**
 * The author's credits in the navigation bar: the total, and the breakdown one click away, with the
 * buy action that opens the purchase form. Shows nothing at all while there is no balance to trust —
 * not loaded yet, or the load failed — rather than a number that may be wrong. While a superAdmin is
 * logged in as another user the buy action is not there: no purchase is made while impersonating, and
 * the server refuses one with 403 should it be sent anyway. Whether the person signed in may buy at all
 * is the purchase form's answer, read as the breakdown opens: when it says no — a trial account, or one
 * without a role — a short line says purchases are available on a paid plan instead, and there is no form
 * to open. The action shows only once the answer is yes.
 *
 * In the bar the indicator is quiet: the toll icon in the primary orange, the number, the unit a shade
 * lighter. The breakdown opens the way the purchase form does: the total on a bgZero panel over the
 * same coin illustration, then the two parts the total is made of, and when the monthly part renews.
 *
 * When the total changes — a generation spent credits, a course finished, a purchase was confirmed —
 * the change floats under it for a moment: red and sinking for credits spent, green and rising for
 * credits added.
 */
export default observer(function CreditsIndicator({ balanceStore, purchaseStore, isImpersonating }: { balanceStore: BalanceStore; purchaseStore: PurchaseStore; isImpersonating: boolean }) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { balance, totalCredits } = balanceStore;
  const change = useCreditChange(totalCredits);

  if (balance === null || totalCredits === null) return null;

  const isOpen = anchorEl !== null;
  const panelColor = theme.palette.bgZero.main;

  return (
    <>
      <Tooltip title='Your credits' placement='bottom'>
        <Button
          onClick={(event) => {
            setAnchorEl(event.currentTarget);
            void purchaseStore.readMayBuy();
          }}
          startIcon={<TollIcon />}
          aria-haspopup='dialog'
          aria-expanded={isOpen}
          aria-controls={isOpen ? BREAKDOWN_ID : undefined}
          sx={{ color: 'inherit', textTransform: 'none', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', '& .MuiButton-startIcon': { color: 'primary.main' } }}
        >
          {/* One inline span for the label, so the space between number and unit is rendered (a flex item's whitespace is not) and the change has the label to centre under. */}
          <span style={{ position: 'relative' }}>
            {formatCount(totalCredits)}{' '}
            <Box component='span' sx={{ color: 'text.secondary', fontWeight: 400 }}>
              {creditsUnit(totalCredits)}
            </Box>
            {change !== null && (
              <FloatingChange key={change.key} delta={change.delta} data-testid='credits-change' aria-hidden='true'>
                {changeLabel(change.delta)}
              </FloatingChange>
            )}
          </span>
        </Button>
      </Tooltip>

      <Divider orientation='vertical' flexItem />
      <Popover
        id={BREAKDOWN_ID}
        open={isOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        // The purchase form's paper: bgTwo, no elevation tint, the same soft black shadow.
        PaperProps={{
          elevation: 0,
          sx: {
            width: BREAKDOWN_WIDTH,
            borderRadius: 1,
            backgroundColor: theme.palette.bgTwo.main,
            color: theme.palette.bgTwo.contrastText,
            backgroundImage: 'none',
            boxShadow: '0 0 15px 0px #000000',
          },
        }}
      >
        <div data-testid='credits-breakdown'>
          {/* The total, on the purchase form's panel: the coins behind it, faded into bgZero on the left so the number reads. */}
          <Box sx={{ position: 'relative', height: PANEL_HEIGHT, backgroundColor: panelColor, overflow: 'hidden' }}>
            <img
              src={currency}
              alt=''
              // A missing file leaves the panel plain; only the broken-image mark goes.
              onError={(event) => {
                event.currentTarget.style.visibility = 'hidden';
              }}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 73%' }}
            />
            <Box sx={{ position: 'absolute', inset: 0, background: `linear-gradient(90deg, ${panelColor} 30%, ${alpha(panelColor, 0.55)} 60%, ${alpha(panelColor, 0)} 100%)` }} />
            <Stack spacing={0.25} sx={{ position: 'absolute', left: 20, top: 20 }}>
              <Typography variant='h4' component='div' sx={{ lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
                {formatCount(totalCredits)}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {creditsUnit(totalCredits)}
              </Typography>
            </Stack>
          </Box>

          <Box sx={{ px: 2.5, pt: 2, pb: 2.5 }}>
            <Box component='dl' sx={{ display: 'grid', gridTemplateColumns: '1fr auto', columnGap: 3, rowGap: 1, m: 0, typography: 'body2' }}>
              <Box component='dt' sx={{ color: 'text.secondary' }}>
                Monthly
              </Box>
              <Box component='dd' sx={{ m: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {formatCount(balance.monthly)}/500
              </Box>
              <Box component='dt' sx={{ color: 'text.secondary' }}>
                Purchased
              </Box>
              <Box component='dd' sx={{ m: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {formatCount(balance.purchased)}
              </Box>
            </Box>
            <Divider sx={{ my: 1.75 }} />
            <Typography variant='caption' component='div' color='text.secondary'>
              Monthly credits renew {renewalDateLabel(balance.nextRenewalUtc)}
            </Typography>
            {!isImpersonating && purchaseStore.mayBuy === false && (
              <Typography variant='caption' component='div' color='text.secondary' sx={{ mt: 1.75 }}>
                {PAID_ROLE_REQUIRED_MESSAGE}
              </Typography>
            )}
            {!isImpersonating && purchaseStore.mayBuy === true && (
              <Button
                variant='contained'
                size='small'
                fullWidth
                sx={{ mt: 1.75, textTransform: 'none' }}
                onClick={() => {
                  setAnchorEl(null);
                  void purchaseStore.open();
                }}
              >
                Buy credits
              </Button>
            )}
          </Box>
        </div>
      </Popover>
    </>
  );
});
